import * as ReactDOMClient from 'react-dom/client';
import RenewalNotice from './Forms/FormRenewalNotice/RenewalNotice';
import ClubEmailPreferences from './Forms/FormClubEmailPreferences/ClubEmailPreferences';
import InlineMessage from '3-components/InlineMessage/InlineMessage';
import useCommunicationPreferencesConfig from './Utils/useCommunicationPreferencesConfig';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import useCommunicationPreferencesValue, { type CommunicationPreferencesFormValue } from './Utils/useCommunicationPreferencesValue';
import { type FC } from 'react';
import HTMLReactParser from 'html-react-parser';

interface CommunicationPreferencesProps {
    warningMessage?: string;
    infoMessage?: string;
}

const CommunicationPreferences: FC<CommunicationPreferencesProps> = ({ warningMessage, infoMessage }) => {
    const { data: configData, isError: isConfigDataError, isLoading: isConfigLoading } = useCommunicationPreferencesConfig();
    const { data: valueData, isError: isValueDataError, isLoading: isValueDataLoading } = useCommunicationPreferencesValue();

    if (isConfigLoading || isValueDataLoading) {
        return (
            <LoadingIndicator className="sg-eg-loader" />
        );
    }

    if (isConfigDataError || isValueDataError || !configData || !valueData) {
        return (
            <div className="communication-preferences-info-message">
                <InlineMessage status="error">
                    Communication Preferences could not be retrieved.
                </InlineMessage>
            </div>
        );
    }

    return (
        <div className="mcc-sections-wrapper">
            {valueData.data.isUnsubscribed && warningMessage && (
                <div className="communication-preferences-info-message">
                    <InlineMessage status="warning">
                        {HTMLReactParser(warningMessage)}
                    </InlineMessage>
                </div>
            )}
            <RenewalNotice
                renewalNoticeConfig={configData.data.find(item => {
                    return item.code === 'RenewalDeliveryMethod';
                })}
                value={valueData.data.renewalDeliveryMethod}
                isWaitingListMember={valueData.data.isWaitingListMember}
            />
            <ClubEmailPreferences
                emailPreferencesConfig={configData.data.filter(item => {
                    return item.code !== 'RenewalDeliveryMethod';
                }).map(config => {
                    const configCode = config.code.charAt(0).toLowerCase() + config.code.slice(1);
                    const rawValue = valueData.data[configCode as keyof CommunicationPreferencesFormValue];
                    return {
                        config,
                        value: typeof rawValue === 'boolean' ? (rawValue ? 'Yes' : 'No') : null,
                    };
                })}
                isWaitingListMember={valueData.data.isWaitingListMember}
            />

            {infoMessage && (
                <div className="communication-preferences-info-message">
                    <InlineMessage status="info">
                        {HTMLReactParser(infoMessage)}
                    </InlineMessage>
                </div>
            )}
        </div>
    );
};

const queryClient = new QueryClient();

interface CommunicationPreferencesInitProps {
    el: HTMLDivElement;
    props: CommunicationPreferencesProps;
}

const CommunicationPreferencesInit = ({ el, props }: CommunicationPreferencesInitProps): void => {
    const root = ReactDOMClient.createRoot(el);

    const warningMessage = props.warningMessage || '';
    const infoMessage = props.infoMessage || '';

    root.render(
        <QueryClientProvider client={queryClient}>
            <CommunicationPreferences infoMessage={infoMessage} warningMessage={warningMessage} />
        </QueryClientProvider>
    );
};

export default CommunicationPreferencesInit;
